<script lang="ts">
  import { computed, inject, provide, type Ref, type InjectionKey } from 'vue'
  import { useSessionStorage } from '@vueuse/core'

  // Constants for session storage keys
  export const SUPPORT_SESSION_STORAGE_KEYS = {
    SESSION_ID: 'Gatship-Support-Session-Id',
    SESSION_TOKEN: 'Gatship-Support-Session-Token'
  } as const

  /** The key for the support session ID in the route query. */
  export const SUPPORT_SESSION_ROUTE_QUERY_KEY = 'support-session-id'

  export function useSupportSessionFromRouteQuery(
    supportSessionId: Ref<string | null>,
    setSupportSessionId: (sessionId: string | null) => void
  ) {
    /**
     * Initializes the support session from the URL query or session storage.
     *
     * Reads `?support-session-id=…` directly from `window.location` rather
     * than through a reactive router helper, so this module stays
     * independent of the app router. After adoption, the query param is
     * stripped via `history.replaceState` to keep it out of subsequent
     * navigations, browser history, and copy-pasted URLs. If there is no
     * support session id, nothing happens.
     */
    async function getSupportSessionIdFromRouteQuery() {
      const url = new URL(window.location.href)
      const sessionIdFromUrl =
        url.searchParams.get(SUPPORT_SESSION_ROUTE_QUERY_KEY) ?? undefined
      const sessionId = sessionIdFromUrl ?? supportSessionId.value
      if (sessionId && typeof sessionId === 'string') {
        setSupportSessionId(sessionId)
        if (sessionIdFromUrl) {
          url.searchParams.delete(SUPPORT_SESSION_ROUTE_QUERY_KEY)
          history.replaceState(history.state, '', url.toString())
        }
      }
    }

    return {
      getSupportSessionIdFromRouteQuery
    }
  }

  /**
   * Centralized composable for managing support session storage.
   *
   * This composable provides a consistent interface for accessing and managing
   * support session data across the application, ensuring all components use
   * the same storage keys and reactive references.
   *
   * @returns An object containing reactive support session storage references and utilities
   *
   * @example
   * ```typescript
   * const { supportSessionId, hasSupportSession, clearSupportSession } = useSupportSessionStorage();
   *
   * // Check if there's an active support session
   * if (hasSupportSession.value) {
   *   console.log('Active session:', supportSessionId.value);
   * }
   *
   * // Clear the session
   * clearSupportSession();
   * ```
   */
  export function useSupportSessionStorage() {
    // Reactive session storage for support session ID
    const supportSessionId = useSessionStorage<string | null>(
      SUPPORT_SESSION_STORAGE_KEYS.SESSION_ID,
      null
    )

    // Reactive session storage for support session token
    const supportSessionToken = useSessionStorage<string | null>(
      SUPPORT_SESSION_STORAGE_KEYS.SESSION_TOKEN,
      null
    )

    // Computed property to check if the current session is a support session
    const isSupportSession = computed(() => {
      return (
        supportSessionId.value !== null && supportSessionId.value !== undefined
      )
    })

    /**
     * Sets the support session ID in storage.
     *
     * @param sessionId - The support session ID to store
     */
    function setSupportSessionId(sessionId: string | null) {
      supportSessionId.value = sessionId
    }

    /**
     * Sets the support session token in storage.
     *
     * @param token - The support session token to store
     */
    function setSupportSessionToken(token: string | null) {
      supportSessionToken.value = token
    }

    /**
     * Clears both support session ID and token from storage.
     */
    function clearSupportSession() {
      supportSessionId.value = null
      supportSessionToken.value = null
    }

    /**
     * Gets the raw support session ID value (non-reactive).
     * Useful for scenarios where you need the value but don't want reactivity.
     *
     * @returns The current support session ID or null
     */
    function getSupportSessionId(): string | null {
      return supportSessionId.value
    }

    /**
     * Gets the raw support session token value (non-reactive).
     * Useful for scenarios where you need the value but don't want reactivity.
     *
     * @returns The current support session token
     */
    function getSupportSessionToken(): string | null {
      return supportSessionToken.value
    }

    return {
      // Reactive references
      supportSessionId,
      supportSessionToken,

      // Computed properties
      isSupportSession,

      // Utility functions
      setSupportSessionId,
      setSupportSessionToken,
      clearSupportSession,
      getSupportSessionId,
      getSupportSessionToken,

      // Storage keys (for cases where direct access is needed)
      STORAGE_KEYS: SUPPORT_SESSION_STORAGE_KEYS
    }
  }

  export function createSupportSessionContext() {
    const supportSessionStorage = useSupportSessionStorage()
    const supportSessionFromRouteQuery = useSupportSessionFromRouteQuery(
      supportSessionStorage.supportSessionId,
      supportSessionStorage.setSupportSessionId
    )

    return {
      ...supportSessionStorage,
      ...supportSessionFromRouteQuery,

      // Storage keys (for cases where direct access is needed)
      STORAGE_KEYS: SUPPORT_SESSION_STORAGE_KEYS
    }
  }

  export type SupportSessionContext = ReturnType<
    typeof createSupportSessionContext
  >

  export const supportSessionContextKey = Symbol(
    'gs-support-session-context'
  ) as InjectionKey<SupportSessionContext>

  /**
   * @param options - Options for the support session.
   * @param options.inject - If false, the support session will not be injected into the context, and will only use the storage.
   * @returns The support session context.
   */
  export function useSupportSession<
    T extends { inject?: boolean } = { inject?: boolean }
  >(
    options?: T
  ): T extends { inject: false }
    ? ReturnType<typeof useSupportSessionStorage>
    : SupportSessionContext {
    type Result = T extends { inject: false }
      ? ReturnType<typeof useSupportSessionStorage>
      : SupportSessionContext
    if (options?.inject === false) {
      return useSupportSessionStorage() as Result
    }
    const instance = inject(supportSessionContextKey)

    if (!instance) {
      throw new Error('Support session context not found')
    }

    return instance as Result
  }
</script>
<script setup lang="ts">
  const supportSessionContext = createSupportSessionContext()
  provide(supportSessionContextKey, supportSessionContext)
  defineExpose(supportSessionContext)
</script>
<template>
  <slot />
</template>
